import type { FactRow, FactScope, MonthSeries, RangeMetrics, SeriesSet } from '../types/dashboard';
import { PROGRAM_PRICE } from '../data/dimensions';
import { FACTS, TERRITORY_MIX } from './factData';
import { MONTH_COUNT, programSeasonOfMonth } from './calendar';
import { comboProgram } from './combos';
import { territoryById } from './territory';

/** Program season/year for a registration purchased in month m (programs start the following month). */
const PROGRAM_SEASON = Array.from({ length: MONTH_COUNT + 3 }, (_, m) => programSeasonOfMonth(m));
const zeros = () => new Array<number>(MONTH_COUNT).fill(0);
const lagged = () => [zeros(), zeros(), zeros()];

export function emptySeries(): MonthSeries {
  return { members: zeros(), conv: lagged(), regs: lagged(), rev: lagged() };
}

export function emptySeriesSet(): SeriesSet {
  return { all: emptySeries(), Digital: emptySeries(), Local: emptySeries() };
}

/**
 * One pass over the fact table. Member counts are never narrowed by sport / program /
 * season / year — members are only tied to a program through a qualifying registration.
 * Those filters narrow registrations, revenue and the unique members who registered.
 */
export function computeGrouped(scope: FactScope, keyFn: (r: FactRow) => string = () => 'all'): Map<string, SeriesSet> {
  const out = new Map<string, SeriesSet>();
  const factors = new Map<string, { share: number; rev: number }>();
  for (const r of FACTS) {
    if (!scope.territoryIds.has(r.t)) continue;
    if (scope.aopIds && !scope.aopIds.has(r.a)) continue;
    if (scope.source !== 'All' && r.src !== scope.source) continue;
    let f = factors.get(r.t);
    if (!f) {
      const mix = TERRITORY_MIX.get(r.t) ?? [];
      const pf = territoryById.get(r.t)?.priceFactor ?? 1;
      let share = 0;
      let rev = 0;
      mix.forEach((w, k) => {
        if (scope.comboMask[k]) {
          share += w;
          rev += w * PROGRAM_PRICE[comboProgram(k)] * pf;
        }
      });
      f = { share, rev };
      factors.set(r.t, f);
    }
    const key = keyFn(r);
    let set = out.get(key);
    if (!set) {
      set = emptySeriesSet();
      out.set(key, set);
    }
    for (const s of [set.all, set[r.src]]) {
      s.members[r.m] += r.members;
      for (let l = 0; l < 3; l++) {
        const ps = PROGRAM_SEASON[r.m + l];
        if (scope.seasons && !scope.seasons.has(ps.season)) continue;
        if (scope.years && !scope.years.has(ps.year)) continue;
        s.conv[l][r.m] += r.conv[l] * f.share;
        s.regs[l][r.m] += r.regs[l] * f.share;
        s.rev[l][r.m] += r.regs[l] * f.rev;
      }
    }
  }
  return out;
}

export function computeSeries(scope: FactScope): SeriesSet {
  return computeGrouped(scope).get('all') ?? emptySeriesSet();
}

/**
 * Same-period metrics: members added in month m count their registrations with lag l
 * only when m + l is still inside the range. Optionally divided per unit
 * (active territories / AOPs) month by month; a month with 0 units is skipped.
 */
export function perUnitRange(s: MonthSeries, start: number, end: number, units: (m: number) => number): RangeMetrics {
  let members = 0, regMembers = 0, regs = 0, rev = 0;
  let rawM = 0, rawRM = 0, rawR = 0;
  for (let m = Math.max(0, start); m <= end; m++) {
    const u = units(m);
    if (u <= 0) continue;
    members += s.members[m] / u;
    rawM += s.members[m];
    for (let l = 0; l < 3; l++) {
      if (m + l <= end) {
        regMembers += s.conv[l][m] / u;
        regs += s.regs[l][m] / u;
        rev += s.rev[l][m] / u;
        rawRM += s.conv[l][m];
        rawR += s.regs[l][m];
      }
    }
  }
  return {
    members,
    registeringMembers: regMembers,
    registrations: regs,
    revenue: rev,
    conversionRate: rawM ? rawRM / rawM : 0,
    regsPerMember: rawRM ? rawR / rawRM : 0,
  };
}

export function rangeMetrics(s: MonthSeries, start: number, end: number): RangeMetrics {
  return perUnitRange(s, start, end, () => 1);
}
